/**
 * Isometric line drawings for the AI automation process (Kartläggning,
 * Prototyp, Bygge och integration, Drift och ansvar). Same projection, frame,
 * stroke and animation hooks as the other service drawings, with their own
 * motifs and motion:
 * - 01 a chain of process steps on a plate; each step lights up in turn
 * - 02 a first module is assembled on a pedestal, block by block
 * - 03 two systems are joined by a channel; a packet travels between them
 * - 04 a water clock tower fills up steadily and its top pulses
 */

import type { CSSProperties } from "react";

import {
  box,
  CORNER_CROSSES,
  floorGrid,
  IllustrationFrame,
  motionPath,
  polyline,
  project,
  type Point3,
  type ProcessIllustration,
  type Shape,
} from "@/components/services/seo/seo-process-illustrations";

function dotAt(point: Point3) {
  const [cx, cy] = project(point);
  return { cx, cy };
}

/** A flat rectangle on a vertical face at depth y (spans x and z). */
function faceRect(x: number, y: number, z: number, w: number, h: number) {
  return polyline(
    [
      [x, y, z],
      [x + w, y, z],
      [x + w, y, z + h],
      [x, y, z + h],
    ],
    true,
  );
}

// ---------------------------------------------------------------- 01 Kartläggning

/** Process steps on the plate, in order (x, y of each block). */
const STEPS: [number, number][] = [
  [10, 58],
  [30, 40],
  [50, 58],
  [70, 40],
  [86, 62],
];
const STEP_SIZE = 10;
const STEP_HEIGHT = 8;

function MappingIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 20, 0, 100, 64, 4),
    ...STEPS.flatMap(([x, y]) => box(x - STEP_SIZE / 2, y - STEP_SIZE / 2, 4, STEP_SIZE, STEP_SIZE, STEP_HEIGHT)),
  ];

  const tops = STEPS.map(([x, y]) => [x, y, 4 + STEP_HEIGHT] as Point3);

  const construct = [
    ...floorGrid(100, 20).filter((_, index) => index % 2 === 0),
    polyline(STEPS.map(([x, y]) => [x, y, 4] as Point3)),
  ];

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <g className="cc-ill-auto-map__links">
        {tops.slice(1).map((top, index) => (
          <path
            key={index}
            d={polyline([tops[index], top])}
            pathLength={1}
            style={{ "--cc-ill-segment": index } as CSSProperties}
          />
        ))}
      </g>
      {tops.map((top, index) => (
        <circle
          key={index}
          className="cc-ill-auto-map__node"
          style={{ "--cc-ill-node": index } as CSSProperties}
          {...dotAt(top)}
          r={3}
        />
      ))}
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Prototyp

function PrototypeIllustration({ maskId }: { maskId: string | null }) {
  const base: Shape[] = [...box(10, 10, 0, 80, 80, 4), ...box(32, 32, 4, 36, 36, 8)];
  const block = (x: number, y: number, z: number) => box(x, y, z, 14, 14, 10);

  const construct = [
    polyline([[32, 32, 12], [32, 32, 50]]),
    polyline([[68, 68, 12], [68, 68, 50]]),
    polyline([[10, 96, 0], [90, 96, 0]]),
  ];

  return (
    <IllustrationFrame
      layers={[
        { shapes: base },
        { className: "cc-ill-auto-proto__block cc-ill-auto-proto__block--1", shapes: block(36, 36, 12) },
        { className: "cc-ill-auto-proto__block cc-ill-auto-proto__block--2", shapes: block(50, 36, 12) },
        { className: "cc-ill-auto-proto__block cc-ill-auto-proto__block--3", shapes: block(43, 43, 22) },
      ]}
      construct={construct}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <path className="cc-ill-auto-proto__face" d={faceRect(46, 43, 25, 8, 4)} />
      <circle className="cc-ill__dot" {...dotAt([50, 50, 32])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 03 Bygge och integration

const CHANNEL_Z = 18;
/** The packet's path along the channel from system A to system B. */
const PACKET_PATH: Point3[] = [
  [28, 50, CHANNEL_Z],
  [50, 50, CHANNEL_Z],
  [72, 50, CHANNEL_Z],
];

function IntegrationIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 26, 0, 100, 48, 4),
    // system A and system B
    ...box(8, 38, 4, 20, 24, 30),
    ...box(72, 38, 4, 20, 24, 22),
    // channel on two supports
    ...box(28, 47, CHANNEL_Z - 2, 44, 6, 2),
    ...box(42, 48, 4, 4, 4, CHANNEL_Z - 6),
    ...box(56, 48, 4, 4, 4, CHANNEL_Z - 6),
  ];

  const construct = [
    polyline([[8, 38, 34], [92, 38, 34]]),
    polyline([[50, 26, 4], [50, 74, 4]]),
  ];

  const [endX, endY] = project(PACKET_PATH[PACKET_PATH.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-auto-int__line" d={polyline(PACKET_PATH)} pathLength={1} />
      <g className="cc-ill-auto-int__packet" transform={`translate(${endX.toFixed(1)} ${endY.toFixed(1)})`}>
        <rect x={-3.5} y={-3.5} width={7} height={7} rx={1.5} />
        <animateMotion
          begin="indefinite"
          dur="1.6s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(PACKET_PATH)}
        />
      </g>
      <circle className="cc-ill__dot" {...dotAt([18, 50, 34])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Drift och ansvar

const TOWER_X = 40;
const TOWER_Y = 40;
const TOWER_FACE_Y = TOWER_Y;
const TOWER_H = 46;

function OperationIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 24, 0, 100, 56, 4),
    ...box(TOWER_X - 4, TOWER_Y - 4, 4, 28, 20, 4),
    ...box(TOWER_X, TOWER_Y, 8, 20, 12, TOWER_H),
    { d: faceRect(TOWER_X + 4, TOWER_FACE_Y, 12, 12, TOWER_H - 8) },
  ];

  const construct = [
    ...[16, 26, 36, 46].map((z) => polyline([[TOWER_X + 20, TOWER_FACE_Y, 8 + z], [TOWER_X + 30, TOWER_FACE_Y, 8 + z]])),
    polyline([[10, TOWER_Y + 30, 4], [92, TOWER_Y + 30, 4]]),
  ];

  const top = project([TOWER_X + 10, TOWER_Y + 6, 8 + TOWER_H]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-auto-op__level" d={faceRect(TOWER_X + 4, TOWER_FACE_Y, 12, 12, TOWER_H - 14)} />
      <circle className="cc-ill-auto-op__pulse" cx={top[0]} cy={top[1]} r={3} />
      <circle className="cc-ill__dot" cx={top[0]} cy={top[1]} r={2.6} />
    </IllustrationFrame>
  );
}

export const AUTO_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  MappingIllustration,
  PrototypeIllustration,
  IntegrationIllustration,
  OperationIllustration,
];
